import httpStatus from 'http-status';
import { errorMessages } from './errorMessages';
import { EventBusEvents } from './enums';
import EventBus from '@/utils/eventBus';

export const generateHttpDialogMessage = ({
  status,
  error,
  request,
  response,
}) => {
  // check CloudFront error
  if (response?.headers?.server === 'CloudFront' && status === 403) {
    return {
      title: '権限エラー',
      body: `アクセス権限がありません。<br>許可されていない操作またはネットワークからの接続です。<br>操作またはネットワークをご確認ください。`,
      hideCloseIcon: false,
      code: error.errorCode,
    };
  }

  // target URL
  if (request?.url) {
    if (isUserInvitationConflict(request, error)) {
      // POST https://{domain}/api/um/v1/user/invitation
      return {
        title: 'リクエストエラー',
        body: `このメールアドレスは組織にすでに登録されています。`,
        hideCloseIcon: false,
        code: error.errorCode,
      };
    } else if (isPowerFrequencyConflictError(request, error)) {
      // PUT https://{domain}/cbes/ccs/v1/camera/{cameraId}/setting/imaging/powerFrequency
      return {
        title: 'リクエストエラー',
        body: `前回の設定変更処理が実行中のため、<br>設定が反映されるまでお待ちください。`,
        hideCloseIcon: false,
        code: error.errorCode,
      };
    } else if (isCognitoAuthenticationFailure(request, error)) {
      // POST https://{domain}/cbes/ccs/v1/user/cognitoAuth
      return {
        title: ' ',
        body: `お客様のアカウントは、現在どの組織にも所属していないため、ログインができません。<br/>
 組織への招待メールが届いている場合は、招待メール記載のリンクをクリックして登録を行ってください。`,
        hideCloseIcon: false,
      };
    } else if (isUpdateUserAvatarNameFailure(request, error)) {
      // PUT https://{domain}/cbes/ccs/v1/user/avatar/name
      return {
        title: 'エラー',
        body: `既に同じ値が設定されているか、<br/>設定できない文字列が含まれています。<br/>
別のユーザー名で登録してください。`,
        hideCloseIcon: false,
      };
    }
  }

  // check specific error codes before general status checks
  if (status === 500 && error?.errorCode === 99003) {
    EventBus.emit(EventBusEvents.AuthFailureCleanup, {
      reason: 'Account access denied - database inconsistency',
      timestamp: new Date().toISOString(),
      source: 'httpErrorMessage',
    });
    return {
      title: 'サーバーエラー',
      body: `お客様のアカウントではこの組織にアクセスできません。<br>
        ログアウトします。`,
      hideCloseIcon: false,
      code: error.errorCode,
    };
  }

  // if status error exist then return
  if (errorMessages.httpStatus[status]) return errorMessages.httpStatus[status];

  switch (status) {
    case 403:
      return {
        title: '権限エラー',
        body: `アクセス権限がありません。<br>
          許可されていない操作です。`,
        hideCloseIcon: false,
        code: error.errorCode,
      };
    case 400:
      return {
        title: 'リクエストエラー',
        body: `エラーが発生しました。<br>
          操作または入力内容をご確認ください。`,
        hideCloseIcon: false,
        code: error.errorCode,
      };

    default:
      break;
  }

  switch (httpStatus[`${status}_CLASS`]) {
    case httpStatus.classes.INFORMATIONAL:
      // The responseCode is 1xx
      break;
    case httpStatus.classes.SUCCESSFUL:
      // The responseCode is 2xx
      break;
    case httpStatus.classes.REDIRECTION:
      // The responseCode is 3xx
      break;
    case httpStatus.classes.CLIENT_ERROR:
      // The responseCode is 4xx
      return {
        title: 'エラー',
        body: `予期しないエラーが発生しました。<br>
          しばらくたってからもう一度お試しください。`,
        hideCloseIcon: false,
        code: error.errorCode,
      };
    case httpStatus.classes.SERVER_ERROR:
      // The responseCode is 5xx
      if (status === 503 && error.errorCode === 101001) {
        return {
          title: 'エラーがあります。',
          body: `カメラが正常に動作していない可能性があります。<br>
            カメラの状態をご確認ください。`,
          hideCloseIcon: false,
        };
      } else if (status === 504 && error.errorCode === 111001) {
        return {
          title: 'エラーがあります。',
          body: `カメラが正常に動作していない可能性があります。<br>
            カメラの状態をご確認ください。`,
          hideCloseIcon: false,
        };
      }

      return {
        title: 'サーバーエラー',
        body: `サーバーエラーのため、アクセスできませんでした。<br>
          しばらくたってからもう一度お試しください。`,
        hideCloseIcon: false,
      };

    default:
      // Unknown
      break;
  }

  return null;
};

/** *******************
 * Error handlers
 * ***************** */

function isUserInvitationConflict(request, error) {
  return (
    request.url.endsWith('api/um/v1/user/invitation') &&
    request.method.toLowerCase() === 'post' &&
    error.status === 409 &&
    error.errorCode === 71000
  );
}

function isPowerFrequencyConflictError(request, error) {
  return (
    request.url.endsWith('/setting/imaging/powerFrequency') &&
    request.method.toLowerCase() === 'put' &&
    error.status === 409 &&
    error.errorCode === 71002
  );
}

function isCognitoAuthenticationFailure(request, error) {
  return (
    request.url.endsWith('v1/user/cognitoAuth') &&
    request.method.toLowerCase() === 'post' &&
    error.status === 401 &&
    error.errorCode === 20000
  );
}

function isUpdateUserAvatarNameFailure(request, error) {
  return (
    request.url.endsWith('v1/user/avatar/name') &&
    request.method.toLowerCase() === 'put' &&
    error.status === 400 &&
    error.errorCode === 11004
  );
}
/********************************* */
